import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, BookOpen, Settings2, CheckCircle2,
  Clock3, AlertCircle, ArrowRight, RefreshCw,
  Sparkles, ListChecks, LockKeyhole, Package,
  Store, Warehouse, Users, Truck, Wallet,
  Landmark, Layers3, BriefcaseBusiness,
} from "lucide-react";
import api from "@/lib/api";

const itemMeta = {
  company_profile: {
    icon: Building2,
    description: "Identitas, jenis usaha, skala bisnis, dan informasi perusahaan.",
  },
  configuration: {
    icon: Settings2,
    description: "Preferensi dan konfigurasi aplikasi sesuai kebutuhan usaha.",
  },
  business_units: {
    icon: Layers3,
    description: "Unit usaha yang menjadi bagian dari perusahaan.",
  },
  brands: {
    icon: Sparkles,
    description: "Merek yang digunakan dalam kegiatan usaha.",
  },
  outlets: {
    icon: Store,
    description: "Lokasi penjualan dan kegiatan operasional.",
  },
  warehouses: {
    icon: Warehouse,
    description: "Lokasi penyimpanan bahan baku dan barang.",
  },
  departments_positions: {
    icon: BriefcaseBusiness,
    description: "Struktur departemen dan jabatan organisasi.",
  },
  employees: {
    icon: Users,
    description: "Data karyawan yang terlibat dalam operasional.",
  },
  products: {
    icon: Package,
    description: "Produk, barang dagang, bahan baku, dan jasa.",
  },
  customers: {
    icon: Users,
    description: "Data pelanggan untuk transaksi dan pengelolaan piutang.",
  },
  suppliers: {
    icon: Truck,
    description: "Pemasok bahan baku dan kebutuhan operasional.",
  },
  inventory: {
    icon: Package,
    description: "Persediaan, stok awal, dan nilai persediaan.",
  },
  cash_bank: {
    icon: Wallet,
    description: "Kas, rekening bank, dan sumber pembayaran.",
  },
  coa: {
    icon: BookOpen,
    description: "Struktur akun untuk pencatatan dan laporan keuangan.",
  },
  fixed_assets: {
    icon: Landmark,
    description: "Registrasi dan pengelolaan aset tetap perusahaan.",
  },
};

const statusConfig = {
  "Siap": {
    icon: CheckCircle2,
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  "Dalam proses": {
    icon: Clock3,
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
  "Belum dimulai": {
    icon: AlertCircle,
    className: "bg-slate-100 text-slate-600 border-slate-200",
  },
  "Opsional": {
    icon: Sparkles,
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
  "Belum tersedia": {
    icon: LockKeyhole,
    className: "bg-slate-100 text-slate-500 border-slate-200",
  },
};

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig["Belum dimulai"];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${config.className}`}>
      <Icon className="h-3.5 w-3.5" />
      {status}
    </span>
  );
}

function getActionLabel(item) {
  if (item.feature_status === "unavailable" || !item.href) {
    return "Belum tersedia";
  }

  if (item.operational_status === "ready") return "Kelola data";
  if (item.setup_status === "Dalam proses") return "Lanjutkan";
  if (item.setup_status === "Opsional") return "Atur jika diperlukan";

  return "Mulai persiapan";
}

function SetupTask({ item, groupTitle }) {
  const meta = itemMeta[item.key] || {};
  const Icon = meta.icon || Building2;
  const description = meta.description || item.reason || item.title;
  const actionLabel = getActionLabel(item);
  const unavailable = item.feature_status !== "available" || !item.href;

  return (
    <article className="rounded-xl border bg-white p-4 transition-shadow hover:shadow-sm">
      <div className="flex items-start gap-3">
        <div className="shrink-0 rounded-lg border bg-slate-50 p-2.5">
          <Icon className="h-5 w-5 text-slate-700" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900">
              {item.title}
            </h3>
            <StatusBadge status={item.setup_status} />
          </div>

          <p className="mt-1 text-xs text-slate-400">{groupTitle}</p>

          <p className="mt-2 text-sm leading-5 text-slate-600">
            {description}
          </p>

          {item.reason && item.setup_status !== "Siap" && (
            <p className="mt-2 text-xs leading-5 text-amber-700">
              {item.reason}
            </p>
          )}

          {item.count !== null && item.count !== undefined && (
            <p className="mt-2 text-xs text-slate-500">
              {item.count} data terdaftar
            </p>
          )}

          <div className="mt-4 flex items-center justify-between gap-3 border-t pt-3">
            <span className={`text-xs font-medium ${
              item.required ? "text-orange-700" : "text-slate-500"
            }`}>
              {item.required ? "Pekerjaan wajib" : "Sesuai kebutuhan"}
            </span>

            {unavailable ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
                <LockKeyhole className="h-3.5 w-3.5" />
                {actionLabel}
              </span>
            ) : (
              <Link
                to={item.href}
                className="inline-flex items-center gap-1.5 rounded-md text-xs font-semibold text-orange-700 hover:text-orange-800 focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                {actionLabel}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function CompanySetupPage() {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReadiness = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/company-setup/readiness");
      setReadiness(response.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        "Tidak dapat mengambil data persiapan perusahaan."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReadiness();
  }, [loadReadiness]);

  if (loading && !readiness) {
    return (
      <main className="page-shell" data-testid="company-setup-page">
        <div className="flex min-h-64 items-center justify-center gap-3 text-sm text-slate-500">
          <RefreshCw className="h-5 w-5 animate-spin" />
          Memuat persiapan perusahaan...
        </div>
      </main>
    );
  }

  if (error && !readiness) {
    return (
      <main className="page-shell" data-testid="company-setup-page">
        <div className="mx-auto max-w-xl rounded-xl border bg-white p-8 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-red-500" />
          <h2 className="mt-3 font-semibold">Gagal memuat Company Setup</h2>
          <p className="mt-2 text-sm text-slate-500">{error}</p>
          <button
            onClick={loadReadiness}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
          >
            <RefreshCw className="h-4 w-4" />
            Coba lagi
          </button>
        </div>
      </main>
    );
  }

  const summary = readiness?.summary || {};
  const items = readiness?.items || [];
  const groups = readiness?.groups || [];

  const groupTitles = Object.fromEntries(
    groups.map((group) => [group.key, group.title])
  );

  const requiredItems = items
    .filter((item) => item.required)
    .sort((a, b) => {
      const rank = (item) => {
        if (item.feature_status === "unavailable") return 3;
        if (item.operational_status !== "ready") return 0;
        return 1;
      };
      return rank(a) - rank(b);
    });

  const otherItems = items.filter((item) => !item.required);

  const progress = Math.max(
    0,
    Math.min(100, Number(summary.progress_percent) || 0)
  );

  return (
    <main className="page-shell space-y-6" data-testid="company-setup-page">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-400">
          <Building2 className="h-4 w-4" />
          MAZBOY HUB · COMPANY SETUP CENTER
        </div>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Persiapan Data Perusahaan
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Pusat pekerjaan untuk menyiapkan perusahaan sebelum menjalankan operasional.
            </p>
          </div>

          <button
            onClick={loadReadiness}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Perbarui
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Pembaruan data gagal. Menampilkan hasil terakhir yang berhasil dimuat.
          <button onClick={loadReadiness} className="ml-2 font-semibold underline">
            Coba lagi
          </button>
        </div>
      )}

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs text-slate-500">Total pekerjaan</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
            {summary.total_items ?? items.length}
          </p>
          <p className="mt-1 text-xs text-slate-400">Bagian persiapan perusahaan</p>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs text-slate-500">Sudah siap</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
            {summary.ready_items ?? 0}
          </p>
          <p className="mt-1 text-xs text-slate-400">Bagian yang memenuhi persyaratan</p>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs text-slate-500">Progres persiapan wajib</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-orange-600">
            {progress}%
          </p>
          <p className="mt-1 text-xs text-slate-400">
            {summary.ready_required_items ?? 0} dari {summary.required_items ?? 0} pekerjaan wajib
          </p>
        </div>
      </section>

      <section className="rounded-xl border bg-white p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              Progres persiapan perusahaan
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Dihitung berdasarkan pemenuhan seluruh pekerjaan wajib.
            </p>
          </div>
          <span className="text-lg font-bold tabular-nums text-orange-600">
            {progress}%
          </span>
        </div>

        <div
          className="mt-4 h-2.5 overflow-hidden rounded-full bg-slate-100"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progres persiapan perusahaan"
        >
          <div
            className="h-full rounded-full bg-orange-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <p className="mt-3 text-xs text-slate-500">
          {summary.operationally_ready
            ? "Seluruh persyaratan wajib telah terpenuhi."
            : `${summary.required_setup_incomplete ?? 0} pekerjaan wajib masih perlu diselesaikan.`}
        </p>
      </section>

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-12">
        <aside className="space-y-4 lg:col-span-4">
          <section className="rounded-xl border bg-white p-5">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-orange-50 p-2">
                <Sparkles className="h-5 w-5 text-orange-600" />
              </div>
              <h2 className="font-semibold text-slate-900">Selamat datang di MAZBOY HUB</h2>
            </div>

            <p className="mt-3 text-sm leading-6 text-slate-600">
              Lengkapi data perusahaan untuk memulai operasional. Anda dapat
              menyiapkan kebutuhan dasar terlebih dahulu dan mengembangkan
              struktur perusahaan seiring pertumbuhan bisnis.
            </p>
          </section>

          <section className="rounded-xl border bg-white p-5">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-slate-600" />
              <h2 className="text-sm font-semibold text-slate-900">Panduan Memulai</h2>
            </div>

            <div className="mt-4 space-y-4">
              <div className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-50 text-xs font-bold text-orange-700">1</span>
                <div>
                  <p className="text-sm font-medium">Lengkapi profil perusahaan</p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Tentukan identitas, jenis usaha, dan skala bisnis.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-50 text-xs font-bold text-orange-700">2</span>
                <div>
                  <p className="text-sm font-medium">Siapkan data operasional</p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Buat outlet, produk, pemasok, dan data lain sesuai kebutuhan.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-50 text-xs font-bold text-orange-700">3</span>
                <div>
                  <p className="text-sm font-medium">Siapkan fondasi keuangan</p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Periksa COA dan lengkapi saldo awal sebelum operasional.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <Link
            to="/configuration"
            className="flex items-center justify-between gap-3 rounded-xl border bg-white p-4 transition-colors hover:border-orange-300 hover:bg-orange-50/30"
          >
            <div className="flex items-center gap-3">
              <Settings2 className="h-5 w-5 text-slate-600" />
              <div>
                <p className="text-sm font-semibold text-slate-900">Kelola Fitur</p>
                <p className="mt-1 text-xs text-slate-500">
                  Atur preferensi dan konfigurasi aplikasi.
                </p>
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </Link>

          <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
              <Building2 className="h-4 w-4" />
              Bertumbuh sesuai kebutuhan
            </div>
            <p className="mt-2 text-xs leading-5 text-blue-800">
              Usaha sederhana tidak wajib memiliki departemen, banyak karyawan,
              atau gudang pusat. Fitur lanjutan dapat disiapkan ketika diperlukan.
            </p>
          </div>
        </aside>

        <section id="setup-tasks" className="min-w-0 space-y-5 lg:col-span-8">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-slate-700" />
                <h2 className="text-lg font-bold text-slate-900">
                  Pekerjaan Persiapan
                </h2>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Selesaikan pekerjaan wajib terlebih dahulu.
              </p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              {requiredItems.length} wajib
            </span>
          </div>

          <div className="space-y-3">
            {requiredItems.map((item) => (
              <SetupTask
                key={item.key}
                item={item}
                groupTitle={groupTitles[item.group] || ""}
              />
            ))}

            {requiredItems.length === 0 && (
              <p className="rounded-xl border bg-white p-5 text-sm text-slate-500">
                Tidak ada pekerjaan wajib yang ditetapkan untuk model usaha ini.
              </p>
            )}
          </div>

          <div className="border-t pt-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Pekerjaan Opsional & Fitur Lainnya
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  Aktifkan atau lengkapi sesuai kebutuhan perusahaan.
                </p>
              </div>
              <span className="text-xs text-slate-400">
                {otherItems.length} bagian
              </span>
            </div>

            <div className="space-y-3">
              {otherItems.map((item) => (
                <SetupTask
                  key={item.key}
                  item={item}
                  groupTitle={groupTitles[item.group] || ""}
                />
              ))}
            </div>
          </div>
        </section>
      </div>

      <footer className="border-t pt-4 text-xs leading-5 text-slate-400">
        Status dan progres pada halaman ini diperoleh langsung dari pemeriksaan
        kesiapan perusahaan MAZBOY HUB.
      </footer>
    </main>
  );
}
